/**
 * Definitions for the listing strips on the custom landing page.
 *
 * Shared between LandingPage.duck (so loadData can fetch each strip with
 * the URL-derived chip filter on the server) and CustomLandingPage (so the
 * page renders the same strips client-side).
 *
 * Adding a new strip = add an entry here. The duck and the page both pick
 * it up automatically.
 */

export const SECTION_RECENTLY_LISTED = 'recentlyListed';
export const SECTION_DRESSES = 'dresses';

// Kjoler's id in the category tree (second level, under Klær).
const DRESSES_CATEGORY_ID = 'klar-kjoler';

// Client, Week 3 Phase 2: two strips. Kjoler on its own, then everything
// else. `excludeCategoryIds` keeps the two from repeating a listing: the
// recently-listed strip asks the API for every category except Kjoler
// (see buildStripCategoryParams). Adding a strip = add an entry here; the
// duck and the page both pick it up automatically.
export const DRESSES_STRIP = {
  sectionId: SECTION_DRESSES,
  titleMessageId: 'CustomLandingPage.dressesTitle',
  parentCategoryPath: ['klar', DRESSES_CATEGORY_ID],
  hideChips: true,
  lightBg: true,
};

export const RECENTLY_LISTED_STRIP = {
  sectionId: SECTION_RECENTLY_LISTED,
  titleMessageId: 'CustomLandingPage.recentlyListedTitle',
  parentCategoryPath: [],
  excludeCategoryIds: [DRESSES_CATEGORY_ID],
  // Client, Sept 2026: no chip row. The category circles at the top of the
  // page already lead into the same categories, and having both put two
  // category pickers a few centimetres apart.
  hideChips: true,
  lightBg: false,
};

// The duck fetches every strip from this list; the page places them
// individually, because the sell CTA now sits between the two.
export const STRIPS = [DRESSES_STRIP, RECENTLY_LISTED_STRIP];

/**
 * URL query parameter key for a strip's selected chip. Each strip lives
 * under its own namespaced key so two strips can have independent
 * selections in the same URL, e.g.
 * `?strip-recentlyListed=topper&strip-dresses=korte`.
 */
export const stripParamKey = sectionId => `strip-${sectionId}`;

/** 1-based depth of a category id in the tree, or null if it isn't there. */
const findCategoryLevel = (categories, id, level = 1) => {
  for (const category of categories) {
    if (category.id === id) {
      return level;
    }
    const inChildren = findCategoryLevel(category.subcategories || [], id, level + 1);
    if (inChildren) {
      return inChildren;
    }
  }
  return null;
};

/**
 * The category nodes at `level`, narrowed by whatever the params already
 * pin at the levels above (a pinned level keeps only that branch; an
 * unpinned one keeps every branch).
 */
const nodesAtLevel = (categories, level, params) => {
  let nodes = categories;
  for (let l = 1; l < level; l++) {
    const pinned = params[`pub_categoryLevel${l}`];
    nodes = pinned
      ? (nodes.find(n => n.id === pinned)?.subcategories || [])
      : nodes.flatMap(n => n.subcategories || []);
  }
  return nodes;
};

/**
 * Build the SDK `pub_categoryLevel*` params for a strip given its parent
 * path and (optionally) the currently selected chip.
 *
 * - parentCategoryPath: ['klar', 'klar-kjoler'] → always applied
 * - chipKey: subcategory selected by the user, applied at the next level
 * - options.excludeCategoryIds: categories the strip must not show. The
 *   API has no "not this category" filter, so the exclusion is expressed
 *   as an explicit list: at the excluded id's level, every sibling in
 *   scope except the excluded ones, comma-joined (the API reads a list
 *   as "any of"). Needs `options.categories`, the live tree, to know the
 *   siblings. Hidden categories stay in the list on purpose: the strip
 *   is "everything except X", not "everything visible except X". When
 *   nothing in scope is excluded (e.g. the Sko chip and an exclusion
 *   under Klær) no list is sent at all.
 *
 * Empty path + null chipKey + no exclusion returns `{}` (no filter).
 */
export const buildStripCategoryParams = (parentCategoryPath = [], chipKey = null, options = {}) => {
  const { excludeCategoryIds = [], categories = [] } = options;
  const params = {};
  parentCategoryPath.forEach((id, index) => {
    params[`pub_categoryLevel${index + 1}`] = id;
  });
  if (chipKey) {
    params[`pub_categoryLevel${parentCategoryPath.length + 1}`] = chipKey;
  }

  const byLevel = new Map();
  excludeCategoryIds.forEach(id => {
    const level = findCategoryLevel(categories, id);
    if (level) {
      byLevel.set(level, [...(byLevel.get(level) || []), id]);
    }
  });
  byLevel.forEach((excluded, level) => {
    const key = `pub_categoryLevel${level}`;
    if (params[key]) {
      // Already pinned to one id at this level by the path or a chip; a
      // pin is narrower than any exclusion, nothing to add.
      return;
    }
    const scope = nodesAtLevel(categories, level, params).map(n => n.id);
    const kept = scope.filter(id => !excluded.includes(id));
    if (kept.length > 0 && kept.length < scope.length) {
      params[key] = kept.join(',');
    }
  });
  return params;
};
